import { use, useRef, useEffect, ReactNode, useCallback } from "react";
import { defaultLot } from '../../Types/Lot';
import { LotDetailContext } from "../../Contexts/LotDetailContext";
import CentredSpinner from '../../Components/CentredSpinner';
import { Lot } from '../../Types/Lot';

export interface LotDetailsChangeCheckerProps {
    disabled: boolean,
    children: ReactNode,
}

const POLLING_INTERVAL_MS = 10000; // 10 seconds between responses

/**
 * LotDetailsChangeChecker polls the backend to ensure that we are displaying a reasonably up to date version of the lot details page
 * It checks if there's any changes on a regular basis and if it finds there are changes it fetches the latest version of the lot
 */
export function LotDetailsChangeChecker({ disabled, children }: LotDetailsChangeCheckerProps) {
    const {
        lotDetail,
        loadingLotDetails: loading,
        getLotDetail,
        getLotQualities,
        getLotGenealogy,
        checkForLotChanges
    } = use(LotDetailContext);

    const abortControllerRef = useRef<AbortController | null>(null);
    const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
    const currentLotNumberRef = useRef<string | undefined>(undefined);

    /* 
     * Need to keep track of the current lotDetail in a ref so that we can use it in the polling function 
     * else the lotDetail becomes stale and the changes comparison happens with the old version
     */
    const currentLotRef = useRef(lotDetail);
    useEffect(() => {
        currentLotRef.current = lotDetail;
    }, [lotDetail]);

    // Abort any in-flight requests when lotDetail changes (e.g., lot number changed)
    useEffect(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    }, [lotDetail?.summary?.lotNumber]);

    const checkForChanges = useCallback(async (lotDetail: Lot) => {
        if (lotDetail?.summary?.lotNumber) {
            abortControllerRef.current = new AbortController();
            try {
                const lotHasChanged = await checkForLotChanges(
                    lotDetail.summary.lotNumber,
                    lotDetail.summary.latestRevisionId,
                    lotDetail.summary.currentStatus,
                    lotDetail.summary.processingStatus,
                    lotDetail.gradingComments.length,
                    lotDetail.packages.length,
                    abortControllerRef.current.signal);

                if (lotHasChanged) {
                    console.log("The lot has changed. Updating lot details..");
                    getLotDetail(undefined, true);
                    getLotQualities(undefined, true);
                    getLotGenealogy(undefined, true);
                }

                // Schedule next check after successful response
                if (lotDetail?.summary?.lotNumber) {
                    timeoutIdRef.current = setTimeout(() => {
                        checkForChanges(currentLotRef.current);
                    }, POLLING_INTERVAL_MS);
                }
            } catch (error) {
                // Silently ignore abort errors
                if (error instanceof DOMException && error.name === 'AbortError') {
                    return;
                }

                throw error;
            } finally {
                abortControllerRef.current = null;
            }
        }
    }, [checkForLotChanges, getLotDetail, getLotGenealogy, getLotQualities])

    // Polling logic: wait for response before scheduling next check
    useEffect(() => {
        const lotNumber = lotDetail?.summary?.lotNumber;

        // Only clear and restart polling if lot number actually changed
        if (!disabled && !loading && currentLotNumberRef.current !== lotNumber) {
            currentLotNumberRef.current = lotNumber;
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
                timeoutIdRef.current = null;
            }

            checkForChanges(lotDetail);
        }
    }, [checkForChanges, disabled, loading, lotDetail]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
            }
        };
    }, []);

    if (loading) {
        return <CentredSpinner />;
    }

    return (
        <>
            {children}
        </>
    );
}
